import React, { useMemo } from "react";
import { Form } from "react-bootstrap";
import type { TPortion } from "@/types";
import { useTranslation } from "react-i18next";
import { pickLocalizedText } from "@/utils/languageVersions/pickLocalizedText";

interface DishPortionSelectorProps {
  portions: TPortion[];
  currency: string;
  unit: string;
  selectedPortionId: number | null;
  onChangeSelectedPortionId: (portionId: number) => void;
}

const DishPortionSelector: React.FC<DishPortionSelectorProps> = ({
  portions,
  currency,
  unit,
  selectedPortionId,
  onChangeSelectedPortionId,
}) => {
  const { t } = useTranslation();
  const sortedPortions = useMemo(() => {
    if (!portions?.length) return [];

    return [...portions].sort((a, b) => {
      const priceDiff = a.price - b.price;
      return priceDiff !== 0 ? priceDiff : a.id - b.id;
    });
  }, [portions]);

  const visiblePortions = useMemo(() => {
    return sortedPortions
      .map((portion) => {
        const localizedName = pickLocalizedText(
          portion.name,
          portion.nameOrigin
        );
        if (!localizedName) return null;

        return {
          ...portion,
          localizedName,
        };
      })
      .filter(
        (portion): portion is NonNullable<typeof portion> => portion != null
      );
  }, [sortedPortions]);

  if (!visiblePortions.length) return null;

  return (
    <>
      <h5 className="fw-bold mb-4">{t("Portion")}</h5>
      <div className="d-flex flex-column gap-4">
        {visiblePortions.map((portion) => {
          const portionVolume =
            portion.volume != null ? `${portion.volume} ${unit}` : null;
          const priceText = `${portion.price} ${currency}`;

          return (
            <Form.Check
              key={portion.id}
              className="m-0 d-flex align-items-center justify-content-between gap-3 ps-0"
            >
              <Form.Check.Label
                htmlFor={`dish-portion-${portion.id}`}
                className="m-0 flex-grow-1"
                style={{ cursor: "pointer" }}
              >
                <span className="fs-14-16">{portion.localizedName}</span>
                {portionVolume ? (
                  <span className="ms-1 fs-14-16">{portionVolume}</span>
                ) : null}
                <span className="fw-bold fs-14-16 ms-1">{priceText}</span>
              </Form.Check.Label>

              <Form.Check.Input
                id={`dish-portion-${portion.id}`}
                type="radio"
                name="dish-portion"
                className="m-0 flex-shrink-0"
                checked={selectedPortionId === portion.id}
                onChange={() => onChangeSelectedPortionId(portion.id)}
              />
            </Form.Check>
          );
        })}
      </div>
    </>
  );
};

export default React.memo(DishPortionSelector);
